/**
 * Platform Admin Model Registry Service
 * 
 * Service for managing ML models across all tenants (platform admin only).
 * Connects to BRAIN backend /api/platform-admin/ml/models endpoints.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Uses getBaseUrl() for dynamic domain resolution
 * - Defensive null checks on all API responses
 * - Proper error handling with try/catch
 */

import { getBaseUrl } from '../shared/utils/apiConfig';

export interface ModelMetadata {
  model_id: string;
  version: number;
  tenant_id: string;
  name: string;
  description: string;
  model_type: string;
  deployment_status: 'pending' | 'deploying' | 'deployed' | 'failed' | 'stopped';
  created_at: string;
  updated_at: string;
  path: string;
  size_bytes: number;
  metrics: Record<string, unknown>;
  tags: string[];
  is_production: boolean;
  access_count: number;
  last_accessed: string | null;
}

export interface ModelListResponse {
  success: boolean;
  models: ModelMetadata[];
  total_count: number;
  filters: {
    tenant_id: string | null;
    deployment_status: string | null;
    include_deprecated: boolean;
  };
}

export interface ModelMetadataResponse {
  success: boolean;
  metadata: ModelMetadata;
}

export interface RegistryStats {
  tenant_id: string;
  total_models: number;
  status_counts: Record<string, number>;
  total_size_bytes: number;
  total_access_count: number;
  registry_created_at: string;
  registry_updated_at: string;
}

export interface RegistryStatsResponse {
  success: boolean;
  stats: RegistryStats | {
    message: string;
    note: string;
  };
}

// Network configuration constants
const NETWORK_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY_MS: 1000, // 1 second
  REQUEST_TIMEOUT_MS: 30000, // 30 seconds
} as const;

class PlatformAdminModelRegistryService {
  private baseUrl: string;
  private maxRetries: number = NETWORK_CONFIG.MAX_RETRIES;
  private retryDelay: number = NETWORK_CONFIG.RETRY_DELAY_MS;

  constructor() {
    this.baseUrl = getBaseUrl();
  }

  /**
   * Get authorization headers with platform admin token
   */
  private getHeaders(): HeadersInit {
    const token = localStorage.getItem('astrapeak_platform_admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  /**
   * Retry fetch with exponential backoff for network errors
   */
  private async fetchWithRetry(
    url: string,
    options: RequestInit,
    retries: number = this.maxRetries
  ): Promise<Response> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), NETWORK_CONFIG.REQUEST_TIMEOUT_MS);
        
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        const isLastAttempt = attempt === retries;
        
        // Check if it's a network error (not HTTP error)
        if (error instanceof Error) {
          const isNetworkError = 
            error.name === 'TypeError' || 
            error.name === 'AbortError' ||
            error.message.includes('fetch') ||
            error.message.includes('network');
          
          if (isNetworkError && !isLastAttempt) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = this.retryDelay * Math.pow(2, attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
        }
        
        // Re-throw on last attempt or non-network errors
        throw error;
      }
    }
    
    throw new Error('Max retries exceeded');
  }

  /**
   * List all models with optional filters
   */
  async listModels(
    tenantId?: string,
    deploymentStatus?: string,
    includeDeprecated: boolean = false
  ): Promise<ModelListResponse> {
    const params = new URLSearchParams();
    if (tenantId) params.append('tenant_id', tenantId);
    if (deploymentStatus) params.append('deployment_status', deploymentStatus);
    params.append('include_deprecated', includeDeprecated.toString());

    const url = `${this.baseUrl}/api/platform-admin/ml/models/list?${params.toString()}`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to list models: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Defensive null checks
    return {
      success: data.success ?? false,
      models: Array.isArray(data.models) ? data.models : [],
      total_count: data.total_count ?? 0,
      filters: data.filters ?? {
        tenant_id: null,
        deployment_status: null,
        include_deprecated: false
      }
    };
  }

  /**
   * Get detailed metadata for a specific model
   */
  async getModelMetadata(modelId: string, tenantId: string): Promise<ModelMetadataResponse> {
    const params = new URLSearchParams({ tenant_id: tenantId });
    const url = `${this.baseUrl}/api/platform-admin/ml/models/${modelId}/metadata?${params.toString()}`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Model ${modelId} not found for tenant ${tenantId}`);
      }
      throw new Error(`Failed to get model metadata: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Update deployment status of a model
   */
  async updateDeploymentStatus(
    modelId: string,
    tenantId: string,
    deploymentStatus: string
  ): Promise<ModelMetadataResponse> {
    const params = new URLSearchParams({
      tenant_id: tenantId,
      deployment_status: deploymentStatus
    });
    const url = `${this.baseUrl}/api/platform-admin/ml/models/${modelId}/deployment-status?${params.toString()}`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'PUT',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 400) {
        const error = await response.json();
        throw new Error(error.detail || 'Invalid deployment status');
      }
      if (response.status === 404) {
        throw new Error(`Model ${modelId} not found for tenant ${tenantId}`);
      }
      throw new Error(`Failed to update deployment status: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Delete a model from registry
   */
  async deleteModel(
    modelId: string,
    tenantId: string,
    force: boolean = false
  ): Promise<{ success: boolean; message: string }> {
    const params = new URLSearchParams({
      tenant_id: tenantId,
      force: force.toString()
    });
    const url = `${this.baseUrl}/api/platform-admin/ml/models/${modelId}?${params.toString()}`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 400) {
        const error = await response.json();
        throw new Error(error.detail || 'Cannot delete production model without force=true');
      }
      if (response.status === 404) {
        throw new Error(`Model ${modelId} not found for tenant ${tenantId}`);
      }
      throw new Error(`Failed to delete model: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get registry statistics
   */
  async getRegistryStats(tenantId?: string): Promise<RegistryStatsResponse> {
    const params = tenantId ? new URLSearchParams({ tenant_id: tenantId }) : new URLSearchParams();
    const url = `${this.baseUrl}/api/platform-admin/ml/models/stats?${params.toString()}`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get registry stats: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get all production models
   */
  async getProductionModels(tenantId?: string): Promise<ModelListResponse> {
    const params = tenantId ? new URLSearchParams({ tenant_id: tenantId }) : new URLSearchParams();
    const url = `${this.baseUrl}/api/platform-admin/ml/models/production?${params.toString()}`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get production models: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Defensive null checks
    return {
      success: data.success ?? false,
      models: Array.isArray(data.production_models) ? data.production_models : [],
      total_count: data.total_count ?? 0,
      filters: {
        tenant_id: tenantId ?? null,
        deployment_status: 'production',
        include_deprecated: false
      }
    };
  }
}

// Export singleton instance
export const platformAdminModelRegistryService = new PlatformAdminModelRegistryService();
export default platformAdminModelRegistryService;
